import { z } from 'zod'

/**
 * The signed-in member, as the chrome needs them.
 *
 * Only what the top nav and the sidebar member card render — the plan drives
 * the FREE chip, the "Upgrade to Plus" link and every Plus upsell on Home, so
 * it is part of identity here rather than a separate billing query.
 */
export const planSchema = z.enum(['free', 'plus', 'pro'])

export const meSchema = z.object({
  name: z.string().min(1),
  /** "Sita Sharma" — the avatar menu's header; `name` is the first name alone. */
  fullName: z.string().min(1),
  plan: planSchema,
  /** Absolute URL, or null when the avatar falls back to initials. */
  avatar: z.string().nullable(),
})

/**
 * The Account family's own view of the member — everything the hub's profile
 * card and the Edit profile page put on screen.
 *
 * `phone` and `email` are nullable because the backend's own `AuthUser` has
 * them nullable: a social sign-in carries an email and no phone, and an
 * older account can carry a phone and no email. Null means "the backend has
 * none", which the screens render as an absent line rather than an em dash —
 * a blank where a phone number goes reads as a loading bug.
 */
export const memberProfileSchema = z.object({
  fullName: z.string().min(1),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  /**
   * Whether the member has been through the SMS code.
   *
   * Mapped from `user.otp_verified`, which is the only verification flag on
   * the wire and is set by the OTP we text to the phone. There is no
   * equivalent for email, so Edit profile says nothing about the email's
   * state rather than guessing at one.
   */
  phoneVerified: z.boolean(),
  /**
   * False for an account made by social sign-in: it has no password to
   * change, only one to set. Off `/me` alone — the login response does not
   * carry it, which is why it lives here and not on the chrome's `Me`.
   */
  hasPassword: z.boolean(),
})

/** The language mm-be serves company summaries in (`/auth/preferences/`). */
export const languageSchema = z.enum(['en', 'ne'])

/** GET/PATCH /auth/preferences/. */
export const preferencesSchema = z.object({ language: languageSchema })

/** POST /auth/account/delete/. A literal `true`: a refusal arrives as an error. */
export const accountDeletedSchema = z.object({ deleted: z.literal(true) })

/**
 * Security's Change password, before it is sent. The 8-character floor is the one rule of mm-be's
 * policy the client can know for sure; the rest (common, all digits, too like your name) come
 * back as PASSWORD_TOO_WEAK with mm-be's own reasons.
 */
export const changePasswordSchema = z
  .object({
    current: z.string().min(1, 'Enter your current password'),
    next: z.string().min(8, 'Use at least 8 characters'),
    again: z.string(),
  })
  .refine((form) => form.again === form.next, {
    path: ['again'],
    error: 'The two passwords do not match',
  })

export type ChangePasswordInput = z.infer<typeof changePasswordSchema>

export type Plan = z.infer<typeof planSchema>
export type Language = z.infer<typeof languageSchema>
export type Preferences = z.infer<typeof preferencesSchema>
export type Me = z.infer<typeof meSchema>
export type MemberProfile = z.infer<typeof memberProfileSchema>
